/**
 * Component to display a single dag.
 *
 */
import React, { useEffect, useState } from "react"
import { Button, Col, Drawer, Empty, message, Row, Typography } from "antd"
import useapi from "@/hooks/useapi"
import { fetchJsonById, uploadJson } from "@/api/dags.api"
import LoadingIndicator from "@components/spinners/loading"
import DagsForm from "@components/forms/dags"
import { EditOutlined, PlusSquareOutlined } from "@ant-design/icons"
import {
  convertDataConsumableByForm,
  IFormSchemaOutput,
} from "@/utils/form.utils"
import JsonEditor from "@components/forms/dags/jsonEditor"
import { ErrorBoundary } from "react-error-boundary"

// @ts-ignore
function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>Oops. There seems to be issue/s with dags:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  )
}

const DagSingle = ({
  dagId,
  mode,
  schema,
  dagName,
}: {
  dagId: string
  schema: IFormSchemaOutput
  mode: "update" | "create"
  dagName: string
}) => {
  const {
    data = [],
    loading,
    error,
    request,
  } = useapi(() => fetchJsonById(dagId))

  const [visible, setVisible] = useState(false)
  const [formSchema, setFormSchema] = useState<IFormSchemaOutput[]>([])

  useEffect(() => {
    let formSchema = convertDataConsumableByForm(schema)
    // @ts-ignore
    setFormSchema(formSchema)
  }, [])

  const showDrawer = () => {
    request()
    if (mode === "update") {
      request()
    }
    setVisible(true)
  }

  const onClose = () => {
    setVisible(false)
  }

  const handleFormSubmission = async (formData: any) => {
    const modData = {
      dagid: dagId,
      dagname: dagName,
      data: formData.data,
    }
    try {
      await uploadJson(modData)
      message.success("Dags uploaded successfully")
      onClose()
    } catch (e) {
      message.error("Error uploading dags")
    }
  }

  const renderContent = () => {
    if (loading) return <LoadingIndicator loading={loading} />

    //FIXME: Check error code - 404 for file not found. Otherwise, this message will be shown for all error thrown
    //  from the backend. Tip: Update useApi hook to record error code.
    if (error && mode === "update")
      return <Empty description={"Error: Couldn't fetch json from server"} />

    const href = `${process.env.REACT_APP_FILESERVER}api/jsons/${data?.dagid}`

    // @ts-ignore
    const jsonFormSubmit = (value) => setFormSchema(value)

    return (
      <>
        {mode === "update" && (
          <a target={"_blank"} href={href}>
            {href}
          </a>
        )}

        <Row gutter={10}>
          <Col
            span={14}
            style={{ height: "calc(100vh - 120px)", overflow: "scroll" }}
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={() => {
                // reset the state of our app so the error doesn't happen again
                let formSchema = convertDataConsumableByForm(schema)
                setFormSchema(formSchema)
              }}
            >
              <DagsForm
                schema__={formSchema}
                onFinish={handleFormSubmission}
                initialValues={mode === "update" && data}
              />
            </ErrorBoundary>
          </Col>
          <Col span={10}>
            <Typography.Title level={5}>
              <i>Experimental</i> Dags Editor
            </Typography.Title>
            <JsonEditor initial={formSchema} onSubmit={jsonFormSubmit} />
          </Col>
        </Row>
      </>
    )
  }

  return (
    <div>
      <Button
        type="primary"
        onClick={showDrawer}
        icon={mode === "update" ? <EditOutlined /> : <PlusSquareOutlined />}
        size="small"
        style={{ margin: "0 2px" }}
      >
        {mode === "create" ? "New" : mode}
      </Button>

      <Drawer
        title={
          loading
            ? "Loading ..."
            : `${data?.dagname} |  ${data?.dagid}` || "DAGS"
        }
        placement="right"
        onClose={onClose}
        visible={visible}
        width={"100%"}
        destroyOnClose
      >
        {renderContent()}
      </Drawer>
    </div>
  )
}

export default DagSingle
